import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faEraser } from '@fortawesome/pro-regular-svg-icons/faEraser';
import { faFloppyDisk } from '@fortawesome/pro-regular-svg-icons/faFloppyDisk';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Col, Dropdown, Row, Segmented, Space, theme } from 'antd';
import type { Key } from 'antd/es/table/interface';
import { t } from 'i18next';
import { useServiceAvailability } from 'pos-client/components/locations/hooks/useServiceAvailability.tsx';
import { PosTable } from 'pos-client/components/shared/Display/Table/PosTable.tsx';
import { useNotifications } from 'pos-client/hooks/useNotifications.tsx';
import { useTableData } from 'pos-client/hooks/useTableData.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import type { LocationModel } from 'pos-shared/models/locationSchema.ts';
import {
  type ServiceTypesRequestModel,
  ServiceTypesRequestSchema,
} from 'pos-shared/models/schedulingSchema.ts';
import type {
  ServiceAvailability,
  ServiceAvailabilityInfoModel,
  ServiceMatrixFEModel,
} from 'pos-shared/models/serviceAvailabilitySchema.ts';

import {
  getBulkActionsOptions,
  getServiceAvailabilityTableColumns,
} from './utils/ServiceAvailabilityTableColumns.tsx';

const LocationMatrixTable = () => {
  const { showSuccess, showError } = useNotifications();
  const { token } = theme.useToken();

  const [serviceTypeFilter, setServiceTypeFilter] =
    useState<ServiceTypesRequestModel>(
      ServiceTypesRequestSchema.options[1] as ServiceTypesRequestModel
    );
  const [tableData, setTableData] = useState<ServiceMatrixFEModel[]>([]);
  const [filteredTableData, setFilteredTableData] = useState<
    ServiceMatrixFEModel[]
  >([]);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [selectedServices, setSelectedServices] = useState<Key[]>([]);
  const { location } = useOutletContext<{
    location: LocationModel;
  }>();

  const {
    isLoading,
    servicesData,
    serviceMatrixConfiguration,
    initalData,
    filterData,
    refetchServicesData,
  } = useServiceAvailability(location.id);

  const { mutate: updateServices, isLoading: isUpdateServicesLoading } =
    trpc.locations.updateServiceAvailability.useMutation({
      onSuccess: () => {
        showSuccess(
          t('common.notification.success.message', {
            action: 'saved',
            entity: 'available services',
          })
        );
        refetchServicesData();
      },
      onError: () => {
        showError(
          t('common.notification.error.message', {
            action: 'save',
            entity: 'available services',
          })
        );
      },
    });

  const setSelections = (checked: boolean, times: string[]) => {
    const newTableData = [...filteredTableData];
    newTableData.forEach((service: ServiceMatrixFEModel) => {
      const { productCategoryId } = service;
      service.availabilities.forEach((availability: ServiceAvailability) => {
        const day = availability.day;

        const serviceMatched = (selectedServices as Key[]).includes(
          productCategoryId
        );

        const dayMatched = (selectedDays as string[]).includes(day);
        if (serviceMatched || dayMatched) {
          if (times.includes('AM')) {
            const foundAvailability = availability.timeslots.find(
              (timeslot) => timeslot.time === 'AM'
            );
            if (foundAvailability) {
              foundAvailability.isSelected = checked;
            }
          }
          if (times.includes('PM')) {
            const foundAvailability = availability.timeslots.find(
              (timeslot) => timeslot.time === 'PM'
            );
            if (foundAvailability) {
              foundAvailability.isSelected = checked;
            }
          }
          if (times.includes('ALLDAY')) {
            const foundAvailability = availability.timeslots.find(
              (timeslot) => timeslot.time === 'ALLDAY'
            );
            if (foundAvailability) {
              foundAvailability.isSelected = checked;
            }
          }
        }
      });
    });

    setFilteredTableData(newTableData);
  };

  const selectBulkAction = (action: string) => {
    switch (action) {
      case 'enableAll':
        setSelections(true, ['AM', 'PM', 'ALLDAY']);
        break;
      case 'enableAM':
        setSelections(true, ['AM']);
        break;
      case 'enablePM':
        setSelections(true, ['PM']);
        break;
      case 'enableAllDay':
        setSelections(true, ['ALLDAY']);
        break;
      case 'disableAll':
        setSelections(false, ['AM', 'PM', 'ALLDAY']);
        break;
      case 'disableAM':
        setSelections(false, ['AM']);
        break;
      case 'disablePM':
        setSelections(false, ['PM']);
        break;
      case 'disableAllDay':
        setSelections(false, ['ALLDAY']);
        break;
    }
  };

  const saveChanges = () => {
    const productCategoryIdsInFiltered = new Set(
      filteredTableData.map((item) => item.productCategoryId)
    );
    const combinedData = [
      ...filteredTableData,
      ...tableData.filter(
        (item) => !productCategoryIdsInFiltered.has(item.productCategoryId)
      ),
    ];

    const selectedServices: ServiceAvailabilityInfoModel[] = [];
    combinedData?.forEach((data) => {
      const timeSlotSettingIds: string[] = [];
      data.availabilities.forEach((availability) => {
        availability.timeslots.forEach((timeslot) => {
          if (timeslot.isSelected) {
            timeSlotSettingIds.push(timeslot.timeslotId);
          }
        });
      });

      if (timeSlotSettingIds.length > 0) {
        selectedServices.push({
          jobTypeId: data.productCategoryId,
          availabilityTimeIds: timeSlotSettingIds,
        });
      }
    });

    updateServices({
      serviceLocationId: location.id,
      serviceAvailabilities: selectedServices,
    });
  };

  const initialiseSelections = (newTableData: ServiceMatrixFEModel[]) => {
    const data = initalData(newTableData);
    setTableData(data);

    const filtered = filterData(newTableData, serviceTypeFilter);
    setFilteredTableData(filtered);
  };

  const toggleDay = (day: string, checked: boolean) => {
    let newSelectedDays;
    if (checked) {
      newSelectedDays = [...selectedDays, day];
    } else {
      newSelectedDays = selectedDays.filter((item) => item !== day);
    }
    setSelectedDays(newSelectedDays);
  };

  const toggleAvailability = (
    serviceId: string,
    timeslotId: string,
    checked: boolean
  ) => {
    const newTableData = [...filteredTableData];
    const foundRow = newTableData?.find(
      (row) => row.productCategoryId === serviceId
    );

    const foundCell: ServiceAvailability | undefined =
      foundRow?.availabilities?.find((availability) =>
        availability.timeslots.find(
          (timeslot) => timeslot.timeslotId === timeslotId
        )
      );

    if (foundCell) {
      const foundAvailability = foundCell?.timeslots?.find(
        (x) => x.timeslotId === timeslotId
      );

      if (foundAvailability) {
        foundAvailability.isSelected = checked;
      }
      setFilteredTableData(newTableData);
    }
  };

  const columns = useMemo(() => {
    return getServiceAvailabilityTableColumns(
      serviceMatrixConfiguration,
      toggleDay,
      toggleAvailability
    );
  }, [serviceMatrixConfiguration, filteredTableData, selectedDays]);

  const tempTableData: ServiceMatrixFEModel[] = useTableData(
    serviceMatrixConfiguration?.categories,
    (matrix) => matrix.productCategoryId
  );

  useEffect(() => {
    if (tempTableData.length) initialiseSelections(tempTableData);
  }, [serviceMatrixConfiguration, serviceTypeFilter, servicesData]);

  const discardChanges = () => {
    if (tempTableData.length) initialiseSelections(tempTableData);
  };

  const handleServiceTypeChange = (
    selectedOption: ServiceTypesRequestModel
  ) => {
    setServiceTypeFilter(selectedOption);
    discardChanges();
  };

  return (
    <>
      <Row justify="space-between" style={{ marginTop: token.margin }}>
        <Col>
          <Segmented
            options={ServiceTypesRequestSchema.options.filter(
              (x) => x === 'Static'
            )}
            defaultValue={serviceTypeFilter}
            onChange={(selectedOption) =>
              handleServiceTypeChange(
                selectedOption as ServiceTypesRequestModel
              )
            }
            style={{ width: 'fit-content', marginRight: token.marginSM }}
          />
          <Dropdown
            menu={{
              items: getBulkActionsOptions(),
              onClick: ({ key }) => selectBulkAction(key),
            }}
          >
            <Button icon={<FontAwesomeIcon icon={faChevronDown} />}>
              {t('common.buttons.bulkActions')}
            </Button>
          </Dropdown>
        </Col>

        <Col>
          <Space>
            <Button
              onClick={discardChanges}
              icon={<FontAwesomeIcon icon={faEraser} />}
            >
              {t('common.buttons.discardChanges')}
            </Button>
            <Button
              onClick={saveChanges}
              type="primary"
              icon={<FontAwesomeIcon icon={faFloppyDisk} />}
              loading={isUpdateServicesLoading}
            >
              {t('common.buttons.save')}
            </Button>
          </Space>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <PosTable
            bordered
            columns={columns}
            dataSource={filteredTableData}
            isLoading={isLoading}
            rowSelection={{
              type: 'checkbox',
              onChange: (selectedRowKeys: Key[]) =>
                setSelectedServices(selectedRowKeys),
            }}
            sticky
          />
        </Col>
      </Row>
    </>
  );
};

export default LocationMatrixTable;
